import { parse, pick } from 'accept-language-parser'
import type { Request } from 'express'

export type Language = 'de' | 'en' | 'es' | 'nl' | 'fr' | 'it'

export const DEFAULT_LANGUAGE: Language = 'en'
export const SUPPORTED_LANGUAGES: Set<Language> = new Set(['de', 'en', 'es', 'nl', 'fr', 'it'] as Array<Language>)

// Unsupported languages whose speakers are better served by a supported one than by DEFAULT_LANGUAGE
const FALLBACK_LANGUAGES: ReadonlyMap<string, Language> = new Map([
  ['ca', 'es'],
  ['eu', 'es'],
  ['gl', 'es'],
])

export function isSupportedLanguage(language: string): language is Language {
  return SUPPORTED_LANGUAGES.has(language as Language)
}

// en-US => en; DE-AT => de; DE => de; de => de
export function getLanguageFromLanguageWithLocale<T = Language>(language: string): T {
  // TODO: Add checking if this really is a locale or just a string
  return language.toLowerCase().split('-')[0] as T
}

/**
 * @deprecated Use getLanguageFromAcceptLanguageHeader instead
 * @param req express request
 */
export function getLanguageFromRequest(req: Request): Language | undefined {
  return getSupportLanguageFromAcceptLanguageHeader(req.headers['accept-language'] || '')
}

/**
 * Returns the language from the accept-language header; undefined if no language is found or none of the found languages is supported.
 * A supported language in the header always wins; only without one, an unsupported language maps to its FALLBACK_LANGUAGES entry (ca-ES -> es).
 * @param acceptLanguageHeader
 */
export function getSupportLanguageFromAcceptLanguageHeader(acceptLanguageHeader: string): Language | undefined {
  return (
    parseAcceptLanguageHeader([...SUPPORTED_LANGUAGES], acceptLanguageHeader, undefined) ??
    getFallbackLanguageFromAcceptLanguageHeader(acceptLanguageHeader)
  )
}

function getFallbackLanguageFromAcceptLanguageHeader(acceptLanguageHeader: string): Language | undefined {
  const fallbackLanguages = parse(acceptLanguageHeader).map(({ code }) => FALLBACK_LANGUAGES.get(code.toLowerCase()))
  return fallbackLanguages.find((language) => language !== undefined)
}

export function parseAcceptLanguageHeader<T>(
  supportLanguages: ReadonlyArray<T>,
  acceptLanguageHeader: string | null,
  defaultLanguage: T,
): T {
  if (!acceptLanguageHeader) {
    return defaultLanguage
  }
  const matchedLanguage = pick([...supportLanguages] as Array<string>, acceptLanguageHeader, { loose: true })
  return matchedLanguage ? getLanguageFromLanguageWithLocale<T>(matchedLanguage) : defaultLanguage
}

/**
 * Without an accept-language header (e.g. crawlers) there is no user preference, so `languageWithoutHeader` is used.
 * A header naming only unsupported languages without a fallback (e.g. Safari sends just `pt-PT`) falls back to DEFAULT_LANGUAGE.
 */
export function getLanguageFromAcceptLanguageHeader(
  acceptLanguageHeader: string | undefined,
  languageWithoutHeader: Language,
): Language {
  if (!acceptLanguageHeader) {
    return languageWithoutHeader
  }
  return getSupportedLanguageOrDefaultFromAcceptLanguageHeader(acceptLanguageHeader)
}

/**
 * Returns the language from the accept-language header; DEFAULT if no language is found or none of the found languages is supported
 * @param acceptLanguageHeader
 */
export function getSupportedLanguageOrDefaultFromAcceptLanguageHeader(
  acceptLanguageHeader: string | undefined,
): Language {
  return (acceptLanguageHeader && getSupportLanguageFromAcceptLanguageHeader(acceptLanguageHeader)) || DEFAULT_LANGUAGE
}
